import { useCallback, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

const STATUS_PILLS = {
  draft: { color: "var(--muted)", bg: "var(--surface-2)" },
  open: { color: "var(--green)", bg: "var(--green-tint)" },
  closed: { color: "#5466ae", bg: "#eef0f8" },
};

function StatusPill({ status }) {
  const s = STATUS_PILLS[status];
  return (
    <span className="pill" style={{ color: s.color, background: s.bg, borderColor: "transparent" }}>
      {status}
    </span>
  );
}

function NewMotionForm({ chapterId, onCreated }) {
  const empty = { title: "", kind: "motion", quorum_pct: "", description: "" };
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    const res = await api(`/api/chapters/${chapterId}/motions`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm(empty);
      onCreated();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not create the vote.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Type
          <select name="kind" value={form.kind} onChange={set("kind")}>
            <option value="motion">Motion (For / Against / Abstain)</option>
            <option value="election">Election (candidates)</option>
          </select>
        </label>
        <label className="field">
          Title
          <input
            name="title"
            required
            value={form.title}
            onChange={set("title")}
            placeholder={form.kind === "election" ? "Election of Treasurer" : "Adopt the revised bylaws"}
          />
        </label>
        <label className="field">
          Quorum % (optional)
          <input
            name="quorum_pct"
            type="number"
            min="1"
            max="100"
            value={form.quorum_pct}
            onChange={set("quorum_pct")}
            placeholder="e.g. 25"
          />
        </label>
        <label className="field">
          Description
          <input name="description" value={form.description} onChange={set("description")} placeholder="Context shown to voters" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit">
          Create draft
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function DraftOptions({ motion, reload }) {
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");

  async function add(e) {
    e.preventDefault();
    if (!label.trim()) return;
    const res = await api(`/api/motions/${motion.id}/options`, {
      method: "POST",
      body: JSON.stringify({ label }),
    });
    if (res.ok) {
      setLabel("");
      reload();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not add the option.");
    }
  }

  async function remove(optionId) {
    await api(`/api/motions/options/${optionId}`, { method: "DELETE" });
    reload();
  }

  return (
    <div style={{ marginTop: 10 }}>
      {motion.options.map((o) => (
        <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ flex: 1 }}>{o.label}</span>
          <button className="btn-danger" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => remove(o.id)} type="button">
            Remove
          </button>
        </div>
      ))}
      <form onSubmit={add} style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <input
          name={`option-${motion.id}`}
          placeholder={motion.kind === "election" ? "Candidate name" : "Option"}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{ width: 220 }}
        />
        <button className="btn-ghost" type="submit">
          Add option
        </button>
      </form>
      {error && <div className="err">{error}</div>}
    </div>
  );
}

function Results({ motion }) {
  const max = Math.max(...motion.results.map((r) => r.votes), 1);
  const winners =
    motion.status === "closed"
      ? motion.results.filter((r) => r.votes === max && r.votes > 0).map((r) => r.id)
      : [];
  return (
    <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
      {motion.results.map((r) => (
        <div key={r.id} style={{ display: "grid", gridTemplateColumns: "180px 1fr 60px", gap: 10, alignItems: "center" }}>
          <span style={{ fontWeight: winners.includes(r.id) ? 700 : 400 }}>
            {r.label}
            {winners.includes(r.id) && " ✓"}
          </span>
          <div className="bar">
            <span style={{ width: `${(r.votes / max) * 100}%` }} />
          </div>
          <span className="muted" style={{ fontSize: 13, textAlign: "right" }}>
            {r.votes}
          </span>
        </div>
      ))}
    </div>
  );
}

function QuorumLine({ motion }) {
  if (motion.eligible_count === null) return null;
  const turnout = `${motion.ballots_cast} of ${motion.eligible_count} eligible voted`;
  if (motion.quorum_needed === null) {
    return (
      <p className="muted" style={{ fontSize: 13, margin: "8px 0 0" }}>
        {turnout} · no quorum requirement
      </p>
    );
  }
  return (
    <p className="muted" style={{ fontSize: 13, margin: "8px 0 0" }}>
      {turnout} · quorum {motion.quorum_pct}% = {motion.quorum_needed} ballots —{" "}
      {motion.quorum_met ? (
        <span style={{ color: "var(--green)", fontWeight: 600 }}>met</span>
      ) : (
        <span style={{ color: "#b42318", fontWeight: 600 }}>not met</span>
      )}
    </p>
  );
}

function MotionCard({ motion, canManage, reload }) {
  async function act(action) {
    const res = await api(`/api/motions/${motion.id}/${action}`, { method: "POST" });
    if (!res.ok) {
      window.alert((await res.json().catch(() => ({}))).error || "That didn't work.");
    }
    reload();
  }

  async function remove() {
    if (!window.confirm(`Delete "${motion.title}" and any ballots cast?`)) return;
    await api(`/api/motions/${motion.id}`, { method: "DELETE" });
    reload();
  }

  return (
    <div className="form-card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ flex: 1 }}>{motion.title}</strong>
        <span className="pill">{motion.kind}</span>
        <StatusPill status={motion.status} />
        {canManage && (
          <span>
            {motion.status === "draft" && (
              <button className="btn-primary" style={{ width: "auto", padding: "6px 12px", fontSize: 13 }} onClick={() => act("open")} type="button">
                Open voting
              </button>
            )}
            {motion.status === "open" && (
              <button className="btn-primary" style={{ width: "auto", padding: "6px 12px", fontSize: 13 }} onClick={() => act("close")} type="button">
                Close voting
              </button>
            )}
            <button className="btn-danger" style={{ padding: "6px 12px", fontSize: 13, marginLeft: 6 }} onClick={remove} type="button">
              Delete
            </button>
          </span>
        )}
      </div>
      {motion.description && (
        <p className="muted" style={{ fontSize: 14, margin: "6px 0 0" }}>
          {motion.description}
        </p>
      )}
      {motion.status === "draft" ? (
        canManage ? (
          <DraftOptions motion={motion} reload={reload} />
        ) : (
          <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
            {motion.options.map((o) => o.label).join(" · ")}
          </p>
        )
      ) : (
        <>
          <Results motion={motion} />
          <QuorumLine motion={motion} />
        </>
      )}
    </div>
  );
}

export function Motions({ chapterId, canManage }) {
  const [data, setData] = useState(null);

  const reload = useCallback(async () => {
    const res = await api(`/api/chapters/${chapterId}/motions`);
    if (res.ok) setData(await res.json());
  }, [chapterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (data === null) return <p className="muted">Loading…</p>;

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Formal member votes — AGM motions and officer elections. Members vote from their
        account area; {data.eligible_now} member{data.eligible_now === 1 ? " is" : "s are"}{" "}
        currently eligible (lifecycle stage member or volunteer).
      </p>

      {canManage && (
        <div className="section">
          <div className="section-head">
            <h3>New vote</h3>
          </div>
          <NewMotionForm chapterId={chapterId} onCreated={reload} />
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <h3>Votes</h3>
        </div>
        {data.motions.length ? (
          data.motions.map((m) => (
            <MotionCard key={m.id} motion={m} canManage={canManage} reload={reload} />
          ))
        ) : (
          <p className="empty">No votes yet — create a motion or an election above.</p>
        )}
      </div>
    </>
  );
}
